import type { TxSummary } from '@apm/contracts';
import { statusLevel } from '@apm/core';
import { TX_TYPE_LABEL } from '../terms';
import { formatFull } from '../time';

/**
 * 끌어 고른 요청을 CSV로 (후보 #7).
 *
 * 보고서에 붙일 때 쓴다. 엑셀이 UTF-8을 알아보게 BOM을 앞에 붙인다 —
 * 없으면 한글 머리글이 깨진 채 열린다.
 *
 * 상한(`SELECTION_LIMIT`)에 잘린 분은 애초에 손에 없다. 화면이 "몇 건 더 있다"를 말하고,
 * 내보내는 것은 지금 표에 보이는 것과 같은 범위다. 보이는 것과 받는 것이 달라선 안 된다.
 */

/** 값 하나를 CSV 칸으로. 쉼표·따옴표·줄바꿈이 있으면 감싼다 */
export function csvCell(value: string | number | undefined): string {
  if (value === undefined) return '';
  const text = String(value);
  if (!/[",\n\r]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

/**
 * 스프레드시트 수식 주입 막기 (49회차, D44-1). `=`·`+`·`-`·`@`로 시작하는 칸을 엑셀이 수식으로
 * 읽는다. 앞에 `'`를 붙이면 글자로 읽힌다 — 대신 값이 한 글자 달라진다. 그래서 **밖에서 온 문자열이
 * 실리는 칸**에만 건다. 숫자·시각·우리가 만든 문구는 그대로다. 처음에는 요청 경로·서비스·인스턴스만
 * 걸었는데 DB 오류 코드·테넌트도 계약상 아무 문자열이라(B-09) 지금은 밖에서 온 문자열 칸 전부에 건다.
 * 앞의 탭·CR 은 엑셀이 건너뛰고 뒤를 수식으로 읽으므로 이 둘로 시작해도 막는다
 */
export function formulaSafe(value: string | undefined): string | undefined {
  if (value === undefined) return undefined;
  return /^[=+\-@\t\r]/.test(value) ? `'${value}` : value;
}

const HEADER = [
  '시작',
  '유형',
  '요청 경로',
  '응답시간(ms)',
  '상태',
  'HTTP 상태',
  'DB 오류 건수',
  'DB 오류 코드',
  '회사(테넌트)',
  '서비스',
  '프로세스',
  '요청 번호',
] as const;

const STATUS_TEXT = { ok: '정상', fail: '실패', error: '오류' } as const;

export function toCsv(rows: readonly TxSummary[]): string {
  const lines = [HEADER.join(',')];
  for (const row of rows) {
    lines.push(
      [
        csvCell(formatFull(row.startedAt)),
        csvCell(TX_TYPE_LABEL[row.txType]),
        csvCell(formulaSafe(row.entry)),
        csvCell(Math.round(row.durationMs)),
        csvCell(STATUS_TEXT[statusLevel(row)]),
        csvCell(row.httpStatus),
        csvCell(row.sqlErrorCount),
        csvCell(formulaSafe(row.sqlErrorCode)),
        csvCell(formulaSafe(row.tenantId)),
        csvCell(formulaSafe(row.service)),
        csvCell(formulaSafe(row.instance)),
        csvCell(formulaSafe(row.traceId)),
      ].join(','),
    );
  }
  return `﻿${lines.join('\r\n')}\r\n`;
}

/** 파일 이름. 언제 받은 것인지 이름만 봐도 알게 한다 */
export function csvFileName(at: number = Date.now()): string {
  return `요청목록-${formatFull(at).replace(/[: ]/g, '-')}.csv`;
}

/** 브라우저가 받게 한다. `<a download>` 한 번 누른 뒤 곧 지운다 */
export function downloadCsv(rows: readonly TxSummary[]): void {
  const blob = new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = csvFileName();
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
